/**
 * FacturationPage — facturation cantine / périscolaire (manager + admin).
 *
 * Deux temps :
 *   1. Aperçu : choisir une famille + un service + une période → calcul détaillé
 *      (repas facturés / non facturés + motif) AVANT d'émettre quoi que ce soit.
 *   2. Émission : génère la facture (refus si une facture existe déjà sur la période).
 * En dessous : la liste des factures avec encaissement / annulation.
 *
 * Tout dépend de l'activation de la facturation (Paramètres). Si elle est
 * désactivée, l'aperçu renvoie 0 repas facturé (le bandeau l'explique).
 */

import { useEffect, useState, useCallback } from 'react';
import { Receipt, Calculator, FileCheck, AlertCircle, Banknote, Ban, FileText } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Button, Field, Input, Alert, Badge } from '@/components/ui';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { useAuth } from '@/lib/auth-context';
import type {
  ScolaireFamille, ServiceFacturable, FacturationApercu, Facture, FactureStatut, MoyenEncaissement,
} from '@roronoa-collectivites/shared-types';

const euros = (c: number) => (c / 100).toFixed(2);
const SERVICES: { value: ServiceFacturable; label: string }[] = [
  { value: 'cantine', label: 'Cantine' }, { value: 'periscolaire', label: 'Périscolaire' }, { value: 'alsh', label: 'ALSH' },
];
const STATUT_VARIANT: Record<FactureStatut, 'info' | 'success' | 'warning' | 'urgent'> = {
  emise: 'info', envoyee: 'info', partiellement_payee: 'warning', payee: 'success', impayee: 'urgent', annulee: 'warning',
};
const STATUT_LABEL: Record<FactureStatut, string> = {
  emise: 'Émise', envoyee: 'Envoyée', partiellement_payee: 'Partiellement payée', payee: 'Payée', impayee: 'Impayée', annulee: 'Annulée',
};

function premierJourMois(): string { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; }
function dernierJourMois(): string { const d = new Date(); const f = new Date(d.getFullYear(), d.getMonth() + 1, 0); return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`; }

export function FacturationPage() {
  const [familles, setFamilles] = useState<ScolaireFamille[]>([]);
  const [familleId, setFamilleId] = useState('');
  const [service, setService] = useState<ServiceFacturable>('cantine');
  const [debut, setDebut] = useState(premierJourMois());
  const [fin, setFin] = useState(dernierJourMois());
  const [apercu, setApercu] = useState<FacturationApercu | null>(null);
  const [factures, setFactures] = useState<Facture[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [servicesMulti, setServicesMulti] = useState<ServiceFacturable[]>(['cantine', 'periscolaire']);
  const [emettantMulti, setEmettantMulti] = useState(false);
  const { confirm, prompt, dialogElement } = useConfirmDialog();
  const { user } = useAuth();
  // Encaisser/annuler = réservé admin/super_admin OU comptable GRC. Le staff cantine
  // (agent/manager non comptable) ne voit pas ces boutons (cohérent avec le serveur).
  const peutEncaisser = user != null && (
    user.role === 'admin' || user.role === 'super_admin' || (user.comptableBriques ?? []).includes('grc')
  );

  useEffect(() => {
    (async () => {
      try {
        const fams = await api.scolaire.listFamilles();
        setFamilles(fams as unknown as ScolaireFamille[]);
        if (fams.length > 0) setFamilleId((fams as unknown as ScolaireFamille[])[0]!.id);
      } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    })();
  }, []);

  const chargerFactures = useCallback(async (fId?: string) => {
    try { setFactures(await api.scolaire.facturation.listFactures(fId ? { familleId: fId } : undefined)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }, []);

  useEffect(() => { void chargerFactures(familleId || undefined); }, [familleId, chargerFactures]);

  async function calculer() {
    if (!familleId) return;
    setLoading(true); setError(null); setOk(null); setApercu(null);
    try { setApercu(await api.scolaire.facturation.apercu(familleId, service, debut, fin)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setLoading(false); }
  }

  async function emettre() {
    if (!familleId) return;
    setError(null); setOk(null);
    try {
      const f = await api.scolaire.facturation.emettre(familleId, { service, periodeDebut: debut, periodeFin: fin });
      setOk(`Facture ${f.numero} émise (${euros(f.montantTtc)} €).`);
      setApercu(null);
      await chargerFactures(familleId);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function emettreGroupee() {
    if (!familleId || servicesMulti.length === 0) return;
    setEmettantMulti(true); setError(null); setOk(null);
    try {
      const f = await api.scolaire.facturation.emettreMulti(familleId, { services: servicesMulti, periodeDebut: debut, periodeFin: fin });
      setOk(`Facture groupée ${f.numero} émise (${euros(f.montantTtc)} €) — ${servicesMulti.length} services.`);
      setApercu(null);
      await chargerFactures(familleId);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setEmettantMulti(false); }
  }

  async function encaisser(f: Facture) {
    const ref = await prompt({ title: `Encaisser ${f.numero}`, description: `Montant dû : ${euros(f.montantTtc)} €. Référence du paiement (chèque, virement…) :`, placeholder: 'CHQ-12345' });
    if (ref === null) return;
    try {
      await api.scolaire.facturation.encaisser(f.id, { moyen: 'cheque' as MoyenEncaissement, montant: f.montantTtc, reference: ref || undefined });
      setOk(`Paiement enregistré pour ${f.numero}.`);
      await chargerFactures(familleId || undefined);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function annuler(f: Facture) {
    const yes = await confirm({ title: `Annuler ${f.numero} ?`, description: 'La facture sera marquée annulée (action de gestion, traçée).', confirmLabel: 'Annuler la facture', danger: true });
    if (!yes) return;
    try { await api.scolaire.facturation.annuler(f.id); await chargerFactures(familleId || undefined); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function telechargerPdf(f: Facture) {
    try {
      const blob = await api.scolaire.facturation.facturePdfBlob(f.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `facture-${f.numero}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'PDF indisponible'); }
  }

  return (
    <ScolaireLayout activeTab="facturation">
      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 space-y-5">
        <div className="flex items-center gap-3">
          <span className="inline-grid h-10 w-10 place-items-center rounded-r-lg bg-cantine-tint text-cantine-fg" aria-hidden="true">
            <Receipt className="h-5 w-5" />
          </span>
          <div>
            <p className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Scolaire / Cantine</p>
            <h1 className="font-display text-xl font-semibold text-ink sm:text-2xl">Facturation</h1>
          </div>
        </div>

        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {ok && <Alert variant="success">{ok}</Alert>}

        {/* Calcul / émission */}
        <Card>
          <CardBody>
            <h2 className="mb-3 font-display text-base font-semibold text-ink">Calculer une facture</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Famille" htmlFor="f-fam">
                <select id="f-fam" value={familleId} onChange={(e) => setFamilleId(e.target.value)}
                  className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                  {familles.map((f) => <option key={f.id} value={f.id}>{f.nom}</option>)}
                </select>
              </Field>
              <Field label="Service" htmlFor="f-svc">
                <select id="f-svc" value={service} onChange={(e) => setService(e.target.value as ServiceFacturable)}
                  className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                  {SERVICES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Du" htmlFor="f-deb"><Input id="f-deb" type="date" value={debut} onChange={(e) => setDebut(e.target.value)} /></Field>
              <Field label="Au" htmlFor="f-fin"><Input id="f-fin" type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></Field>
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="secondary" loading={loading} onClick={() => void calculer()}>
                <Calculator className="h-4 w-4" /> Calculer l'aperçu
              </Button>
              {apercu && apercu.nbRepasFactures > 0 && (
                <Button variant="cantine" onClick={() => void emettre()}>
                  <FileCheck className="h-4 w-4" /> Émettre la facture ({euros(apercu.montantTotal)} €)
                </Button>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Facture groupée multi-services */}
        <Card>
          <CardBody>
            <h2 className="mb-1 font-display text-base font-semibold text-ink">Facture groupée multi-services</h2>
            <p className="mb-3 text-sm text-ink-muted">
              Émet une <strong>facture unique</strong> regroupant plusieurs services sur la période choisie ci-dessus
              (un seul document, un seul paiement, détail par service). Coche les services à inclure.
            </p>
            <div className="flex flex-wrap gap-3">
              {SERVICES.map((s) => (
                <label key={s.value} className="inline-flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={servicesMulti.includes(s.value)}
                    onChange={(e) => setServicesMulti((prev) => e.target.checked ? [...new Set([...prev, s.value])] : prev.filter((v) => v !== s.value))}
                    className="h-4 w-4 accent-[oklch(var(--c-cantine))]" />
                  {s.label}
                </label>
              ))}
            </div>
            <div className="mt-4">
              <Button variant="cantine" loading={emettantMulti} disabled={servicesMulti.length < 1}
                onClick={() => void emettreGroupee()}>
                <FileCheck className="h-4 w-4" /> Émettre la facture groupée ({servicesMulti.length} service{servicesMulti.length > 1 ? 's' : ''})
              </Button>
            </div>
          </CardBody>
        </Card>
        {apercu && (
          <Card>
            <CardBody>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-display text-base font-semibold text-ink">
                  Aperçu — {apercu.familleNom}
                </h2>
                <div className="flex items-center gap-2 text-sm text-ink-muted">
                  <span>QF : {apercu.quotientFamilial ?? '—'}</span>
                  <span>·</span>
                  <span>Tarif : {euros(apercu.tarifApplique)} €</span>
                  <Badge variant="info">{apercu.nbRepasFactures} repas · {euros(apercu.montantTotal)} €</Badge>
                </div>
              </div>
              {apercu.lignes.length === 0 ? (
                <p className="text-sm text-ink-muted">
                  {!apercu.aUnTarif
                    ? 'Aucun tarif défini pour ce service. Ajoutez un tarif dans l\u2019onglet Tarifs pour pouvoir facturer.'
                    : 'Aucun repas réservé ou pointé sur la période pour cette famille.'}
                </p>
              ) : (
                <div className="-mx-1 overflow-x-auto">
                <table className="w-full min-w-[34rem] text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-2xs uppercase tracking-wide text-ink-subtle">
                      <th className="py-2">Enfant</th><th className="py-2">Date</th><th className="py-2">Statut</th>
                      <th className="py-2">Motif</th><th className="py-2 text-right">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apercu.lignes.map((l, i) => (
                      <tr key={i} className={`border-b border-border/60 ${l.facture ? '' : 'text-ink-subtle'}`}>
                        <td className="py-1.5">{l.enfantNom}</td>
                        <td className="py-1.5 tabular-nums">{l.dateService}</td>
                        <td className="py-1.5">{l.statut}</td>
                        <td className="py-1.5 text-ink-muted">{l.motif}</td>
                        <td className="py-1.5 text-right tabular-nums">{l.facture ? `${euros(l.montant)} €` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* Liste des factures */}
        <Card>
          <CardBody>
            <h2 className="mb-3 font-display text-base font-semibold text-ink">
              Factures {familleId && familles.find((f) => f.id === familleId) ? `— ${familles.find((f) => f.id === familleId)!.nom}` : ''}
            </h2>
            {factures.length === 0 ? (
              <p className="text-sm text-ink-muted">Aucune facture pour cette sélection.</p>
            ) : (
              <div className="-mx-1 overflow-x-auto">
              <table className="w-full min-w-[34rem] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-2xs uppercase tracking-wide text-ink-subtle">
                    <th className="py-2">N°</th><th className="py-2">Période</th><th className="py-2">Service</th>
                    <th className="py-2 text-right">Montant</th><th className="py-2">Statut</th><th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {factures.map((f) => (
                    <tr key={f.id} className="border-b border-border/60">
                      <td className="py-2 font-mono text-2xs text-ink">{f.numero}</td>
                      <td className="py-2 tabular-nums text-ink-muted">{f.periodeDebut} → {f.periodeFin}</td>
                      <td className="py-2 text-ink-muted">{f.service === 'multi' ? 'Multi-services' : f.service === 'alsh' ? 'ALSH' : f.service === 'periscolaire' ? 'Périscolaire' : 'Cantine'}</td>
                      <td className="py-2 text-right font-semibold tabular-nums text-ink">{euros(f.montantTtc)} €</td>
                      <td className="py-2"><Badge variant={STATUT_VARIANT[f.statut]}>{STATUT_LABEL[f.statut]}</Badge></td>
                      <td className="py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button type="button" onClick={() => void telechargerPdf(f)} title="Télécharger le PDF"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-r-md text-ink-muted hover:bg-cantine/10 hover:text-cantine focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                            <FileText className="h-4 w-4" />
                          </button>
                          {f.statut !== 'payee' && f.statut !== 'annulee' && peutEncaisser && (
                            <>
                              <button type="button" onClick={() => void encaisser(f)} title="Encaisser"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-r-md text-ink-muted hover:bg-success/10 hover:text-success focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success">
                                <Banknote className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={() => void annuler(f)} title="Annuler"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-r-md text-ink-muted hover:bg-danger/10 hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger">
                                <Ban className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </CardBody>
        </Card>
      </main>
      {dialogElement}
    </ScolaireLayout>
  );
}
